import GLightbox from 'glightbox';
import Swiper from 'swiper';
import {
  Autoplay,
  Navigation,
  Pagination,
  // EffectFade,
  // EffectCoverflow,
  // Parallax,
} from 'swiper/modules';

import type { SectionAjaxData } from './AjaxForm';
import { AjaxForm } from './AjaxForm';
import { choices, swiperColumns } from './helpers';

interface ExtendedElement extends HTMLElement {
  trigger: ExtendedElement;
  target: ExtendedElement;
  offClickHandler: (event: Event) => void;
}

type AppAnimation = {
  from: Keyframe;
  to: Keyframe;
  showTimeout: number;
  hideTimeout: number;
  hoverTimeout: number;
};

type CategoryFilterDataset = {
  category: string;
};

export default class App {
  animation: AppAnimation = {
    from: { opacity: 0, transform: 'scale(0.95)' },
    to: { opacity: 1, transform: 'scale(1)' },
    showTimeout: 100,
    hideTimeout: 75,
    hoverTimeout: 300,
  };

  constructor() {
    this.navObserver();
    this.sectionObserver();

    this.setupCollapsibles();
    this.setupDropdowns();
    this.navDropdown();

    // this.filters();

    this.galleryPopups();
    this.swiper();

    const archive = document.querySelector('.section__archive') as HTMLElement;
    if (archive) {
      new AjaxForm(archive);
    }

    document.querySelectorAll('.js-choices').forEach((element) => {
      choices(element as HTMLSelectElement);
    });
  }

  swiper() {
    const containers =
      document.querySelectorAll<HTMLElement>('.swiper-container');
    containers?.forEach((container) => {
      const swiperElement = container.querySelector('.swiper') as HTMLElement;
      const columns = +(container.dataset.columns ?? 1);
      const autoplay = container.dataset.autoplay === '1';
      const reverse = container.dataset.reverse === '1';
      const breakpoints = swiperColumns(columns);
      let slidesPerView = 1;
      if(container.classList.contains('swiper-gallery')){
        slidesPerView = 2;
      }

      const swiper = new Swiper(swiperElement, {
        // effect: 'coverflow',
        // grabCursor: true,
        // centeredSlides: true,
        slidesPerView,
        loop: true,
        spaceBetween: 10,
        autoplay: autoplay && {
          delay: 3000,
          reverseDirection: reverse,
        },
        breakpoints,
        keyboard: {
          enabled: true,
        },
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
          enabled: true,
        },
        navigation: {
          nextEl: container.querySelector('.swiper-button-next') as HTMLElement,
          prevEl: container.querySelector('.swiper-button-prev') as HTMLElement,
        },
        // coverflowEffect: {
        //   rotate: -20,
        //   stretch: 0,
        //   depth: 100,
        //   modifier: 1,
        //   slideShadows: false,
        // },
        modules: [Navigation, Pagination, Autoplay], //EffectCoverflow
      });
    });
  }

  galleryPopups() {
    GLightbox({
      touchNavigation: true,
      loop: true,
      autoplayVideos: false,
    });
  }

  filters() {
    const listingSections = document.querySelectorAll(
      '.section__archive'
    ) as NodeListOf<HTMLElement>;
    listingSections?.forEach((section) => {
      const filterLists = section.querySelectorAll('.filter-list');
      filterLists?.forEach((list) => {
        const filterElements = list.querySelectorAll('a.filter-link');
        filterElements?.forEach((filter) => {
          filter.addEventListener('click', (e) => {
            e.preventDefault();
            const target = e.currentTarget as HTMLAnchorElement;
            const dataset = target.dataset as CategoryFilterDataset;
            const href = new URL(target.href);
            const category = dataset.category;

            this.setCategoryActiveFilter(filterLists, list, category);
            this.fetch(section, href);
          });
        });
      });
      this.pagination(section);
    });
  }

  pagination(section: HTMLElement) {
    const paginationLinks = section.querySelectorAll('a.page-link');
    paginationLinks?.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const target = e.currentTarget as HTMLAnchorElement;
        const href = new URL(target.href);

        this.fetch(section, href);
      });
    });
  }

  setCategoryActiveFilter(
    filterLists: NodeListOf<Element>,
    activeList: Element,
    category: string
  ) {
    filterLists.forEach((list) => {
      const categoryElement = list.querySelector('.filter-category');
      categoryElement!.innerHTML = '';
    });

    const categoryElement = activeList.querySelector('.filter-category');
    const menu = activeList.querySelector('.filter-menu') as ExtendedElement;
    categoryElement!.innerHTML = category
      ? `: <span class="inline-block ms-1 text-red">${category}</span>`
      : '';
    this.hideDropdown(menu);
  }

  fetch(section: HTMLElement, url: URL) {
    const data = section.dataset as SectionAjaxData;
    const ajaxUrl = data.url;
    const href = url.href;
    const nonce = data._wpnonce;
    const post_type = data.post_type;
    const listing_type = data.listing_type;
    const listing_columns = data.listing_columns;
    const listing_color = data.listing_color;
    const action = data.action;

    const listing = section.querySelector('.listing');
    if (!listing) {
      console.warn('Missing listing element!');
      return;
    }

    url.searchParams.set('url', encodeURIComponent(href));
    url.searchParams.set('action', action);
    url.searchParams.set('_ajax_nonce', nonce);

    url.searchParams.set('post_type', post_type);
    url.searchParams.set('listing_type', listing_type);
    url.searchParams.set('listing_columns', listing_columns);
    url.searchParams.set('listing_color', listing_color);

    section.classList.add('loading');

    fetch(ajaxUrl, {
      method: 'POST',
      // mode: 'same-origin', // no-cors, *cors, same-origin
      // cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
      // credentials: 'same-origin', // include, *same-origin, omit
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      // redirect: 'follow', // manual, *follow, error
      // referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      body: url.searchParams.toString(), // body data type must match "Content-Type" header
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }

        return response.text();
      })
      .then((content) => {
        listing.outerHTML = content;

        const images = section.querySelectorAll('img');
        if (!images.length) {
          section.classList.remove('loading');
        }

        let count = images.length;
        images?.forEach((image) => {
          image.addEventListener('load', (e) => {
            count--;
            if (count === 0) {
              // need wait 2 frames for html to settle ?!
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  section.classList.remove('loading');
                });
              });
            }
          });
        });
        history.pushState({ request: url.searchParams.toString() }, '', href);
        this.pagination(section);
      });
  }

  sectionObserver() {
    // bail if we use the nice scroll animation-timeline
    if (CSS.supports('animation-timeline: view()')) {
      return;
    }

    const thresholdSets = [];
    for (let i = 0; i <= 1.0; i += 0.01) {
      thresholdSets.push(i);
    }

    const sections = document.querySelectorAll('.section-animate');

    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const viewportHeight = window.innerHeight; // or entry.rootBounds.height if root is not null
            const visibleElementHeight = entry.intersectionRect.height;

            // Calculate how much of the viewport height the visible part of the element takes up
            const percentageOfScreenOccupied =
              visibleElementHeight / viewportHeight;

            // Define your desired screen occupation percentage (e.g., 20%)
            const desiredScreenOccupation = 0.2;
            if (
              (entry.boundingClientRect.height / viewportHeight <
                desiredScreenOccupation &&
                entry.intersectionRatio >= 0.9) ||
              percentageOfScreenOccupied >= desiredScreenOccupation
            ) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          }
        });
      },
      {
        root: null, // relative to document viewport
        rootMargin: '0px',
        threshold: thresholdSets,
      }
    );

    sections.forEach((section) => {
      observer.observe(section);
    });
  }

  navObserver() {
    const header = document.querySelector('.header');
    const top = document.querySelector('#to-content');

    if (!header) {
      console.warn('Missing header!');
      return;
    }
    if (!top) {
      console.warn('Missing skip link!');
      return;
    }

    const topObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          header.classList.toggle('is-sticky', !entry.isIntersecting);
        });
      },
      {
        rootMargin: header.classList.contains('home')
          ? '0px 0px 0px 0px'
          : '-200px 0px 0px 0px',
        threshold: 1.0,
      }
    );

    if (window.matchMedia('(max-width: 767px)').matches) {
      header.classList.add('is-sticky');
    }

    if (!header.classList.contains('is-sticky')) {
      topObserver.observe(top);
    }

    window.addEventListener(
      'resize',
      () => {
        if (window.matchMedia('(max-width: 767px)').matches) {
          header.classList.add('is-sticky');
          topObserver.unobserve(top);
        }
      },
      false
    );
  }

  navDropdown() {
    const collapsible = document.querySelectorAll(
      '.navigation-toggle[data-collapse-toggle]'
    );
    collapsible?.forEach((collapseTrigger) => {
      const controlsId = collapseTrigger.getAttribute('data-collapse-controls');
      if (!controlsId) {
        console.warn('Missing controls id for navigation dropdown!');
        return;
      }
      const controlsElement = document.querySelector(controlsId);
      if (!controlsElement) {
        console.warn('Missing controls element for navigation dropdown!');
        return;
      }
      const targetId = collapseTrigger.getAttribute('data-collapse-toggle');
      if (!targetId) {
        console.warn('Missing target id for navigation dropdown!');
        return;
      }
      const targetElement = document.querySelector(`${targetId}`);
      if (!targetElement) {
        console.warn('Missing target for navigation dropdown!');
        return;
      }

      let timer: number | undefined;
      const closeDropdown = () => {
        clearTimeout(timer);
        collapseTrigger.setAttribute('aria-expanded', 'false');
        targetElement.classList.remove('show-navigation');

        // wait for animation to finish
        const items = controlsElement!.querySelectorAll('.navigation-item');
        const count = items.length;
        timer = setTimeout(
          () => {
            // display: none; - kills the animation
            controlsElement?.classList.add('hidden');
          },
          500 + count * 100 // 500ms for nav to fade in and then no of items * 100ms
        );
      };
      const openDropdown = () => {
        clearTimeout(timer);
        collapseTrigger.setAttribute('aria-expanded', 'true');
        // remove display: none; - so we can animate
        controlsElement?.classList.remove('hidden');

        // actually wait for another frame
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            targetElement.classList.add('show-navigation');
          });
        });
      };

      collapseTrigger.addEventListener('click', () => {
        collapseTrigger.getAttribute('aria-expanded') === 'true'
          ? closeDropdown()
          : openDropdown();
      });
      document.addEventListener('keydown', function (e) {
        if (
          e.key === 'Escape' &&
          targetElement.classList.contains('show-navigation')
        ) {
          closeDropdown();
        }
      });
    });
  }

  setupCollapsibles() {
    const collapsible = document.querySelectorAll(
      ':not(.navigation-toggle)[data-collapse-toggle]'
    );
    collapsible?.forEach((collapseTrigger) => {
      const targetId = collapseTrigger.getAttribute('data-collapse-toggle');
      const targetElement = document.querySelector(`#${targetId}`);

      collapseTrigger.addEventListener('click', () => {
        if (collapseTrigger.getAttribute('aria-expanded') === 'true') {
          targetElement!.classList.add('hidden');
          collapseTrigger.setAttribute('aria-expanded', 'false');
        } else {
          targetElement!.classList.remove('hidden');
          collapseTrigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  setupDropdowns() {
    const dropdowns: NodeListOf<ExtendedElement> = document.querySelectorAll(
      '.dropdown, .filter-list'
    );
    const dropdownMenus: NodeListOf<ExtendedElement> =
      document.querySelectorAll('.dropdown-menu, .filter-menu');

    dropdowns?.forEach((dropdownWrapper) => {
      const trigger = dropdownWrapper.querySelector(
        '.dropdown-trigger, .filter-trigger'
      ) as ExtendedElement;
      const target = dropdownWrapper.querySelector(
        '.dropdown-menu, .filter-menu'
      ) as ExtendedElement;
      target.trigger = trigger;
      trigger.target = target;

      trigger.addEventListener('click', () => {
        // event.preventDefault();
        if (target.classList.contains('hidden')) {
          // hide all menus
          this.resetDropdowns(dropdownMenus);
          this.showDropdown(target);
        } else {
          this.hideDropdown(target);
        }
      });
    });
  }

  resetDropdowns(dropdownMenus: NodeListOf<ExtendedElement>) {
    dropdownMenus?.forEach((target) => {
      this.hideDropdown(target);
    });
  }

  hideDropdown(target: ExtendedElement) {
    // bail early if already hidden
    if (target.classList.contains('hidden')) {
      return;
    }

    const trigger = target.trigger;
    const { from, to, hideTimeout } = this.animation;
    // clear off click listener
    const animation = target.animate([to, from], hideTimeout);
    trigger.setAttribute('aria-expanded', 'false');
    this._removeListener(document.body, 'click', target.offClickHandler);
    // after animation hide element
    animation.onfinish = () => {
      target.classList.add('hidden');
    };
  }

  showDropdown(target: ExtendedElement) {
    const trigger = target.trigger;
    const { from, to, showTimeout } = this.animation;
    // display selected menu first for animations to work
    target.classList.remove('hidden');
    trigger.setAttribute('aria-expanded', 'true');
    this.dropdownOffClick(target);
    // wait a frame for the display to kick in
    requestAnimationFrame(() => {
      target.animate([from, to], showTimeout);
    });
  }

  dropdownOffClick(target: ExtendedElement) {
    const trigger = target.trigger;
    // add off click listener
    target.offClickHandler = this._addRemovableListener(
      document.body,
      'click',
      (event) => {
        const eventTarget = event.target as HTMLElement;
        if (
          eventTarget === trigger ||
          eventTarget === target ||
          target.contains(eventTarget) ||
          trigger.contains(eventTarget)
        ) {
          return;
        }
        this.hideDropdown(target);
      }
    );
  }

  _addRemovableListener(
    element: Element,
    type: string,
    cb: (event: Event) => void
  ): (event: Event) => void {
    const handler = (event: Event): void => {
      cb(event);
    };
    element.addEventListener(type, handler);
    return handler;
  }

  _removeListener(
    element: Element,
    type: string,
    cb: (event: Event) => void
  ): void {
    element.removeEventListener(type, cb);
  }
}
